import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { httpService } from "../../services/httpService";
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";
import DynamicTable from "../../components/common/DynamicTable";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { useProfile } from "../../context/ProfileContext";
import LabTestDrawer from "./LabTestDrawer";
import { FaIndianRupeeSign } from "react-icons/fa6";
import ToggleSwitch from "../../components/common/ToggleSwitch"; // Import ToggleSwitch

const MySwal = withReactContent(Swal);

const LabTest = () => {
  const [labTests, setLabTests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLabTestId, setSelectedLabTestId] = useState(null);
  const { isPlanExpired } = useProfile();

  useEffect(() => {
    fetchLabTests();
    fetchCategories();
  }, []);

  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isDrawerOpen) {
      fetchLabTests();
      fetchCategories();
    }
  }, [isDrawerOpen]);

  const fetchLabTests = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-lab-tests`,
      );
      if (response.code === 200) {
        setLabTests(response?.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load lab tests");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-categories`,
      );
      if (response.code === 200) {
        setCategories(response?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteLabTest = async (id) => {
    try {
      const response = await httpService.delete(
        `/vendorvetclinicservice/delete-lab-test/${id}`,
      );
      if (response.status) {
        toast.success("Lab test deleted successfully", {
          position: "top-center",
        });
        fetchLabTests();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete lab test");
    }
  };

  const handleDelete = (id) => {
    MySwal.fire({
      text: "Are you sure you want to delete this lab test? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteLabTest(id);
        MySwal.fire({
          title: "Deleted!",
          text: "Lab test has been deleted.",
          icon: "success",
        });
      }
    });
  };

  const handleEdit = (labTest) => {
    setSelectedLabTestId(labTest._id);
    setIsDrawerOpen(true);
  };

  const handleCopy = (labTest) => {
    MySwal.fire({
      text: "Are you sure you want to copy this lab test?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, copy it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // No dedicated copy endpoint — recreate it via add-lab-test
          const payload = {
            name: `${labTest.name} (Copy)`,
            client_friendly_name: labTest.client_friendly_name || "",
            category_id: labTest.category_id || "",
            description: labTest.description || "",
            sample_type: labTest.sample_type || "",
            test_mode: labTest.test_mode || "internal",
            price: labTest.price ?? 0,
            has_service_charge: labTest.has_service_charge || false,
            tax_percentage: labTest.tax_percentage || 0,
          };
          const response = await httpService.post(
            `/vendorvetclinicservice/add-lab-test`,
            payload
          );
          if (response.status) {
            toast.success("Lab test copied successfully!");
            fetchLabTests();
          }
        } catch (err) {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to copy lab test");
        }
      }
    });
  };

  // Updated status toggle handler for Lab Test
  const handleStatusToggle = (labTest) => {
    const newStatus = labTest.status === "active" ? "inactive" : "active";
    MySwal.fire({
      text: `Are you sure you want to change the status to ${newStatus}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await httpService.put(
            `/vendorvetclinicservice/update-lab-test/${labTest._id}`,
            { status: newStatus }
          );
          if (response.status) {
            toast.success("Lab test status updated successfully!");
            fetchLabTests();
          }
        } catch (err) {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to update status");
        }
      }
    });
  };

  const columns = [
    {
      key: "name",
      header: "Test Name",
      sortable: true,
      renderCell: (row) => (
        <div className="py-2" style={{ minWidth: "200px", maxWidth: "350px" }}>
          <p
            className="text-sm font-medium text-gray-900"
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.5",
            }}
            title={row.name || ""}
          >
            {row.name || "-"}
          </p>
          {row.client_friendly_name && (
            <p className="text-xs text-gray-500 mt-1">
              {row.client_friendly_name}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      renderCell: (row) => {
        // category_id in the API response is a raw id (or null), not populated
        const catId = row.category_id?._id || row.category_id;
        const categoryName = categories.find((c) => c._id === catId)?.name;
        return <span className="text-sm text-gray-700">{categoryName || "-"}</span>;
      },
    },
    {
      key: "test_mode",
      header: "Test Mode",
      sortable: true,
      renderCell: (row) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            row.test_mode === "internal"
              ? "bg-blue-100 text-blue-800"
              : "bg-purple-100 text-purple-800"
          }`}
        >
          {row.test_mode === "internal" ? "Internal" : "External"}
        </span>
      ),
    },
    {
      key: "price",
      header: "Price",
      sortable: true,
      renderCell: (row) => (
        <div className="flex items-center gap-1">
          {row.price != null ? (
            <>
              <FaIndianRupeeSign className="h-3 w-3 text-gray-600" />
              <span className="text-sm font-medium">
                {Number(row.price).toFixed(2)}
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: "service_charge",
      header: "Service Charge",
      sortable: true,
      renderCell: (row) => (
        <div>
          {row.has_service_charge ? (
            <span className="text-sm">Yes ({row.tax_percentage || 0}%)</span>
          ) : (
            <span className="text-sm text-gray-400">No</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: false, // Changed to false since we're using a toggle
      renderCell: (row) => (
        <ToggleSwitch
          isActive={row.status === "active"}
          onChange={() => handleStatusToggle(row)}
          disabled={isPlanExpired}
        />
      ),
    },
  ];

  const actions = [
    {
      label: "Copy",
      icon: (
        <DocumentDuplicateIcon className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
      ),
      onClick: (row) => handleCopy(row),
      colorClasses: "hover:border-blue-500 hover:bg-blue-50",
      shouldShow: () => !isPlanExpired,
    },
    {
      label: "Edit",
      icon: (
        <PencilIcon className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
      ),
      onClick: (row) => handleEdit(row),
      colorClasses: "hover:border-green-500 hover:bg-green-50",
      shouldShow: () => !isPlanExpired,
    },
    {
      label: "Delete",
      icon: (
        <TrashIcon className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" />
      ),
      onClick: (row) => handleDelete(row._id),
      colorClasses: "hover:border-red-500 hover:bg-red-50",
      shouldShow: () => !isPlanExpired,
    },
  ];

  return (
    <div>
      {loading && <LoaderSpiner text="Loading . . ." />}

      <div className="flex flex-wrap items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Lab Tests</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage lab tests with sample type, Test mode, and pricing.
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedLabTestId(null);
            setIsDrawerOpen(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Add New Lab Test
        </button>
      </div>

      <DynamicTable
        columns={columns}
        data={labTests}
        actions={actions}
        showPagination={true}
        initialPageSize={10}
        showPageInfo={true}
        searchable={true}
        searchPlaceholder="Search lab tests by name, sample type..."
      />

      <LabTestDrawer
        isOpen={isDrawerOpen}
        labTestId={selectedLabTestId}
        categories={categories}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedLabTestId(null);
        }}
        onSuccess={() => {
          fetchLabTests();
          fetchCategories();
        }}
      />
    </div>
  );
};

export default LabTest;